import { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  SunIcon,
  MoonIcon,
  LogoutIcon,
  AccountsIcon,
  CalendarIcon,
  PostsIcon,
} from './Icons';
import { useUiStore } from '../store/uiStore';
import { useAuth } from '../hooks/useAuth';
import Avatar from '../features/profile/Avatar';
import NotificationBell from './NotificationBell';
import { useI18n } from '../i18n/I18nProvider';

const headerIconButtonClass =
  'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/60 bg-white/90 text-slate-600 shadow-sm backdrop-blur-sm transition hover:border-blue-200 hover:bg-gradient-to-br hover:from-blue-50/95 hover:to-sky-50/60 hover:text-blue-900 active:scale-[0.97] dark:border-slate-600 dark:bg-slate-900/80 dark:text-slate-200 dark:hover:border-sky-500/40 dark:hover:from-slate-800 dark:hover:to-blue-950/35 dark:hover:text-sky-100 [&_svg]:h-[20px] [&_svg]:w-[20px]';

function ChevronDoubleLeft(props) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="m11 17-5-5 5-5" />
      <path d="m18 17-5-5 5-5" />
    </svg>
  );
}

function MenuIcon(props) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}>
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

function ListIcon(props) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}>
      <line x1="8" y1="6" x2="21" y2="6" />
      <line x1="8" y1="12" x2="21" y2="12" />
      <line x1="8" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3.01" y2="6" />
      <line x1="3" y1="12" x2="3.01" y2="12" />
      <line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>
  );
}

function InboxNavIcon(props) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <path d="M8 10h.01M12 10h.01M16 10h.01" />
    </svg>
  );
}

function MegaphoneIcon(props) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="m3 11 18-5v12L3 14v-3z" />
      <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
    </svg>
  );
}

function pillNavClass(active) {
  return `flex items-center gap-2 rounded-full px-3.5 py-1.5 text-sm font-medium transition [&_svg]:h-[17px] [&_svg]:w-[17px] ${
    active
      ? 'bg-gradient-to-r from-blue-600 to-sky-600 text-white shadow-md shadow-blue-600/25 ring-1 ring-white/25 dark:from-blue-500 dark:to-sky-600 dark:ring-sky-400/25'
      : 'text-slate-600 hover:bg-white/95 hover:text-blue-900 dark:text-slate-400 dark:hover:bg-slate-800/90 dark:hover:text-sky-100'
  }`;
}

function PillNavLink({ to, postsTab, children }) {
  const { pathname, search } = useLocation();
  const sp = new URLSearchParams(search);
  const currentPostsTab = sp.get('tab') || 'scheduled';

  let active = false;
  if (postsTab != null) {
    active = pathname === '/posts' && currentPostsTab === postsTab;
  } else {
    const base = to.split('?')[0];
    active = pathname === base;
  }

  return (
    <NavLink to={to} className={() => pillNavClass(active)}>
      {children}
    </NavLink>
  );
}

export default function Navbar() {
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const sidebarOpen = useUiStore((s) => s.sidebarOpen);
  const theme = useUiStore((s) => s.theme);
  const toggleTheme = useUiStore((s) => s.toggleTheme);
  const { user, logout } = useAuth();
  const { t, language, setLanguage } = useI18n();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onClick = (e) => {
      if (!e.target.closest('[data-header-account]')) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const goToProfile = () => {
    setMenuOpen(false);
    navigate('/profile');
  };

  const pillWrap =
    'mx-auto flex w-full max-w-min min-w-0 items-center justify-center overflow-x-auto rounded-full border border-white/50 bg-slate-100/80 px-1 py-1 shadow-inner shadow-slate-200/50 backdrop-blur-md dark:border-slate-600/50 dark:bg-slate-900/60 dark:shadow-slate-950/50 sm:px-1.5 sm:py-1.5';

  const cycleLanguage = () => setLanguage(language === 'en' ? 'ar' : 'en');

  return (
    <header className="sticky top-0 z-20 border-b border-white/40 bg-white/75 shadow-sm backdrop-blur-xl dark:border-slate-700/50 dark:bg-slate-950/80">
      <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-2.5 px-4 py-2.5 sm:px-5 lg:h-16 lg:flex-row lg:items-center lg:justify-between lg:gap-4 lg:py-0 lg:pl-4 lg:pr-5">
        <div className="flex items-center justify-between gap-3 lg:contents">
          <button
            type="button"
            onClick={toggleSidebar}
            className={`${headerIconButtonClass} lg:hidden`}
            aria-label={sidebarOpen ? t('common.closeMenu') : t('common.openMenu')}
            aria-expanded={sidebarOpen}
          >
            {sidebarOpen ? <ChevronDoubleLeft /> : <MenuIcon />}
          </button>

          <div
            data-header-account
            className="flex shrink-0 flex-row items-center gap-2.5 lg:hidden"
          >
            <HeaderUtilities
              theme={theme}
              toggleTheme={toggleTheme}
              language={language}
              cycleLanguage={cycleLanguage}
              t={t}
              menuOpen={menuOpen}
              setMenuOpen={setMenuOpen}
              user={user}
              goToProfile={goToProfile}
              logout={logout}
              headerIconButtonClass={headerIconButtonClass}
            />
          </div>
        </div>

        <nav
          className="order-3 flex min-w-0 justify-center lg:order-2 lg:flex-1 lg:px-3"
          aria-label={t('common.headerPrimaryNav')}
        >
          <div className={pillWrap}>
            <div className="flex w-max max-w-full min-w-0 items-center gap-0.5 sm:gap-1">
              <PillNavLink to="/calendar">
                <CalendarIcon />
                <span className="hidden sm:inline">{t('common.headerCalendar')}</span>
              </PillNavLink>
              <PillNavLink to="/posts?tab=scheduled" postsTab="scheduled">
                <ListIcon />
                <span className="hidden sm:inline">{t('common.headerQueue')}</span>
              </PillNavLink>
              <PillNavLink to="/posts?tab=draft" postsTab="draft">
                <PostsIcon />
                <span className="hidden sm:inline">{t('common.headerDrafts')}</span>
              </PillNavLink>
              <PillNavLink to="/inbox">
                <InboxNavIcon />
                <span className="hidden sm:inline">{t('common.messagesComments')}</span>
              </PillNavLink>
              <PillNavLink to="/campaigns">
                <MegaphoneIcon />
                <span className="hidden sm:inline">{t('common.campaigns')}</span>
              </PillNavLink>
            </div>
          </div>
        </nav>

        <div
          data-header-account
          className="hidden shrink-0 flex-row items-center justify-end gap-2.5 lg:order-3 lg:flex lg:ps-2"
        >
          <HeaderUtilities
            theme={theme}
            toggleTheme={toggleTheme}
            language={language}
            cycleLanguage={cycleLanguage}
            t={t}
            menuOpen={menuOpen}
            setMenuOpen={setMenuOpen}
            user={user}
            goToProfile={goToProfile}
            logout={logout}
            headerIconButtonClass={headerIconButtonClass}
          />
        </div>
      </div>
    </header>
  );
}

function HeaderUtilities({
  theme,
  toggleTheme,
  language,
  cycleLanguage,
  t,
  menuOpen,
  setMenuOpen,
  user,
  goToProfile,
  logout,
  headerIconButtonClass,
}) {
  return (
    <>
      <button
        type="button"
        onClick={toggleTheme}
        className={headerIconButtonClass}
        aria-label={t('common.toggleTheme')}
      >
        {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
      </button>

      <button
        type="button"
        onClick={cycleLanguage}
        className={`${headerIconButtonClass} font-semibold tracking-tight text-sm text-gray-700 dark:text-gray-200`}
        aria-label={t('common.language')}
        title={language === 'en' ? t('common.english') : t('common.arabic')}
      >
        {language === 'en' ? 'EN' : 'AR'}
      </button>

      <NotificationBell />

      <div className="relative">
        <button
          type="button"
          onClick={() => setMenuOpen((o) => !o)}
          className={`${headerIconButtonClass} !p-0.5`}
          aria-label={t('common.accountMenu')}
          aria-expanded={menuOpen}
        >
          <Avatar user={user} size={34} />
        </button>
        {menuOpen && (
          <div className="dropdown-panel absolute end-0 top-full z-40 mt-2 w-60 animate-pop-in p-1.5">
            <AccountMenuBody
              user={user}
              goToProfile={goToProfile}
              logout={() => {
                setMenuOpen(false);
                logout();
              }}
              t={t}
            />
          </div>
        )}
      </div>
    </>
  );
}

function AccountMenuBody({ user, goToProfile, logout, t }) {
  return (
    <>
      <button
        type="button"
        onClick={goToProfile}
        className="dropdown-item gap-3 text-start"
      >
        <Avatar user={user} size={32} />
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-50">
            {user?.name || t('common.account')}
          </p>
          <p className="truncate text-xs text-slate-500 dark:text-slate-400">{user?.email}</p>
        </div>
      </button>
      <div className="dropdown-divider" />
      <button type="button" onClick={goToProfile} className="dropdown-item gap-2 text-sm">
        <AccountsIcon />
        {t('common.profileSettings')}
      </button>
      <button
        type="button"
        onClick={logout}
        className="dropdown-item gap-2 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/40 dark:hover:text-red-300"
      >
        <LogoutIcon />
        {t('common.signOut')}
      </button>
    </>
  );
}
